import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { Container, FilterSelect, HeaderContainer, Title } from "../styles";
import Table from "../../../components/Table";
import { notification, Select, Tooltip } from "antd";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import Button from "../../../newUXPages/InformaAcademy/components/Button";
import {
  ContentBordered,
  FilterContainer,
  RequiredFieldHint,
} from "./styles";
import { useSelector } from "react-redux";
import {
  getDocumentsAnalyticsService,
  getAnalyticsFiltersService,
  createAnalyticsReportService,
  getAnalyticsReportStatusService,
} from "../../../service/documents-analytics.service";
import {
  HTTP_STATUS,
  NO_ANALYTICS_FOR_FILTERS_CODE,
  POLLING_CONFIG,
  REPORT_STATUS,
} from "../../../constants/analyticsReport";
import { downloadFileFromUrl } from "../../../utils/fileDownload";

const AnalyticsDocuments = () => {
  const intl = useIntl();
  const { Option } = Select;
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [years, setYears] = useState([]);
  const [eventsFiltered, setEventsFiltered] = useState([]);
  const [docsFiltered, setDocsFiltered] = useState([]);
  const { token } = useSelector((state) => state.auth);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const pollingIntervalRef = useRef(null);

  /**
   * @name notifyApiError
   * @param {Object} error - Erro retornado pelo axios
   * @description Exibe a mensagem de erro traduzida a partir do código devolvido pela API. Quando
   * a resposta não traz código de negócio - por exemplo 404 de endpoint inexistente ou falha de
   * rede - cai numa mensagem genérica em vez de exibir "api.error.undefined".
   */
  const notifyApiError = useCallback(
    (error) => {
      const code = error.response?.data?.code;

      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: code
          ? intl.formatMessage({ id: "api.error." + code })
          : intl.formatMessage({ id: "error_loading_filters" }),
      });
    },
    [intl],
  );

  /**
   * @name fetchData
   * @description Busca os dados da tabela. O ano é obrigatório (INFORMACP-4684): sem ano, a tabela
   * fica vazia e a exportação bloqueada.
   */
  const fetchData = useCallback(
    async (
      page = currentPage,
      size = rowsPerPage,
      yearFilter = selectedYear,
      eventFilter = selectedEvents,
      docFilter = selectedDoc,
      sortField = sortBy,
      sortDir = sortDirection,
    ) => {
      if (!yearFilter) {
        setTableData([]);
        setTotalRows(0);
        return;
      }

      setLoadingTable(true);
      try {
        const response = await getDocumentsAnalyticsService({
          token,
          page: page - 1,
          size,
          year: yearFilter,
          eventIds: eventFilter,
          document: docFilter,
          sortDirection: sortDir,
          sortBy: sortField,
        });

        const items = response.data.items || [];
        const pageInfo = response.data.paginationInfo;

        setTableData(items);
        setTotalRows(pageInfo.totalElements || 0);
        setRowsPerPage(pageInfo.maxSize || size);
      } catch (error) {
        const code = error.response?.data?.code;

        // Sem dados para os filtros informados nao e erro: estado vazio.
        if (code === NO_ANALYTICS_FOR_FILTERS_CODE) {
          console.error(error.response?.data?.description);
          setTableData([]);
          setTotalRows(0);
          return;
        }

        notifyApiError(error);
        setTableData([]);
        setTotalRows(0);
      } finally {
        setLoadingTable(false);
      }
    },
    [
      token,
      currentPage,
      rowsPerPage,
      selectedYear,
      selectedEvents,
      selectedDoc,
      sortBy,
      sortDirection,
      notifyApiError,
    ],
  );

  /**
   * @name applyYear
   * @param {number|null} year - Ano da edição selecionado
   * @description Troca o ano: recarrega eventos e documentos elegíveis do ano e reseta as duas
   * seleções para "Todos". Ano nulo (campo limpo) bloqueia consulta e exportação.
   */
  const applyYear = useCallback(
    async (year) => {
      setSelectedYear(year);
      setSelectedEvents([]);
      setSelectedDoc("");
      setCurrentPage(1);

      if (!year) {
        setEventsFiltered([]);
        setDocsFiltered([]);
        setTableData([]);
        setTotalRows(0);
        return;
      }

      setLoadingFilters(true);
      try {
        const response = await getAnalyticsFiltersService({ token, year });
        setEventsFiltered(response.data.events || []);
        setDocsFiltered(response.data.documents || []);
      } catch (error) {
        // Se os filtros falharam, nao consulta a tabela: evita dois erros em sequencia e tabela
        // carregada com os combos vazios.
        notifyApiError(error);
        setEventsFiltered([]);
        setDocsFiltered([]);
        setTableData([]);
        setTotalRows(0);
        return;
      } finally {
        setLoadingFilters(false);
      }

      fetchData(1, rowsPerPage, year, [], "");
    },
    [token, rowsPerPage, fetchData, notifyApiError],
  );

  /**
   * @name applyEvents
   * @param {Array<number>} eventIds - Eventos selecionados (vazio = todos os do ano)
   * @description Recarrega o filtro de documentos com a união dos documentos vinculados aos eventos
   * selecionados e reseta o documento escolhido.
   */
  const applyEvents = useCallback(
    async (eventIds) => {
      setSelectedEvents(eventIds);
      setSelectedDoc("");
      setCurrentPage(1);

      setLoadingFilters(true);
      try {
        const response = await getAnalyticsFiltersService({
          token,
          year: selectedYear,
          eventIds,
        });
        setDocsFiltered(response.data.documents || []);
      } catch (error) {
        // Mesma regra de applyYear: filtros com falha nao disparam a consulta da tabela.
        notifyApiError(error);
        setDocsFiltered([]);
        setTableData([]);
        setTotalRows(0);
        return;
      } finally {
        setLoadingFilters(false);
      }

      fetchData(1, rowsPerPage, selectedYear, eventIds, "");
    },
    [token, selectedYear, rowsPerPage, fetchData, notifyApiError],
  );

  /**
   * @name initFilters
   * @description Carrega os anos disponíveis e seleciona o default: ano vigente quando ele possui
   * evento elegível; caso contrário, o ano mais recente disponível (regra definida pela PO em
   * 08/09/2026, complementando o RF1).
   */
  const initFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const response = await getAnalyticsFiltersService({ token });
      const availableYears = response.data.years || [];
      setYears(availableYears);

      if (availableYears.length === 0) {
        return;
      }

      const currentYear = new Date().getFullYear();
      const defaultYear = availableYears.includes(currentYear)
        ? currentYear
        : availableYears[0];

      applyYear(defaultYear);
    } catch (error) {
      notifyApiError(error);
      setYears([]);
    } finally {
      setLoadingFilters(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    initFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePerRowsChange = (newPerPage, page) => {
    setRowsPerPage(newPerPage);
    fetchData(
      page,
      newPerPage,
      selectedYear,
      selectedEvents,
      selectedDoc,
      sortBy,
      sortDirection,
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(
      page,
      rowsPerPage,
      selectedYear,
      selectedEvents,
      selectedDoc,
      sortBy,
      sortDirection,
    );
  };

  /**
   * @name showNotification
   * @param {string} type - Tipo da notificação (success, error, warning, info)
   * @param {string} messageKey - Chave de tradução da mensagem
   * @param {string} descriptionKey - Chave de tradução da descrição
   * @description Helper para exibir notificações com i18n
   */
  const showNotification = useCallback(
    (type, messageKey, descriptionKey) => {
      notification[type]({
        message: intl.formatMessage({ id: messageKey }),
        description: intl.formatMessage({ id: descriptionKey }),
      });
    },
    [intl],
  );

  /**
   * @name clearPollingInterval
   * @description Limpa o intervalo de polling de forma segura
   */
  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * @name handleReportSuccess
   * @param {string} downloadUrl - URL para download do relatório
   * @description Realiza o download e exibe notificação de sucesso
   */
  const handleReportSuccess = useCallback(
    (downloadUrl) => {
      clearPollingInterval();
      downloadFileFromUrl(downloadUrl);
      setLoadingDownload(false);
      showNotification("success", "success", "file_downloaded_successfully");
    },
    [clearPollingInterval, showNotification],
  );

  /**
   * @name handleReportError
   * @param {string} errorKey - Chave de tradução do erro
   * @description Trata erros e exibe notificação
   */
  const handleReportError = useCallback(
    (errorKey) => {
      clearPollingInterval();
      setLoadingDownload(false);
      showNotification("error", "error", errorKey);
    },
    [clearPollingInterval, showNotification],
  );

  /**
   * @name checkReportStatus
   * @param {string} reportId - ID do relatório
   * @param {number} attempts - Contador de tentativas
   * @description Verifica o status do relatório e decide próxima ação. O status ERROR é tratado
   * separadamente do timeout (INFORMACP-4684): antes, falha de geração aparecia como timeout.
   */
  const checkReportStatus = useCallback(
    async (reportId, attempts) => {
      try {
        const response = await getAnalyticsReportStatusService({
          token,
          reportId,
        });

        const { status, downloadUrl } = response.data;

        if (status === REPORT_STATUS.PROCESSED && downloadUrl) {
          handleReportSuccess(downloadUrl);
        } else if (status === REPORT_STATUS.ERROR) {
          handleReportError("error_generating_analytics_report");
        } else if (attempts >= POLLING_CONFIG.MAX_ATTEMPTS) {
          handleReportError("timeout_generating_file");
        }
      } catch (error) {
        handleReportError("error_checking_file_status");
      }
    },
    [token, handleReportSuccess, handleReportError],
  );

  /**
   * @name startPolling
   * @param {string} reportId - ID do relatório para fazer polling
   * @description Inicia o polling para verificar status do relatório. Limpa um polling anterior
   * antes de criar o novo: sem isso, um segundo disparo da exportação sobrescrevia a referência do
   * intervalo e o primeiro ficava rodando até a página ser desmontada - problema que a janela de
   * 10 minutos deixaria durar bem mais.
   */
  const startPolling = useCallback(
    (reportId) => {
      clearPollingInterval();

      let attempts = 0;

      pollingIntervalRef.current = setInterval(() => {
        attempts++;
        checkReportStatus(reportId, attempts);
      }, POLLING_CONFIG.INTERVAL_MS);
    },
    [checkReportStatus, clearPollingInterval],
  );

  /**
   * @name requestDownloadAnalyticsFile
   * @returns {Promise<void>}
   * @description Realiza o processo de criação e download do arquivo de analytics
   */
  const requestDownloadAnalyticsFile = useCallback(async () => {
    setLoadingDownload(true);

    try {
      const response = await createAnalyticsReportService({
        token,
        year: selectedYear,
        eventIds: selectedEvents,
        document: selectedDoc,
      });

      if (response.status !== HTTP_STATUS.SUCCESS) {
        handleReportError("error_creating_file_for_download");
        return;
      }

      const { reportId } = response.data;
      startPolling(reportId);
    } catch (error) {
      handleReportError("error_creating_file_for_download");
    }
  }, [
    token,
    selectedYear,
    selectedEvents,
    selectedDoc,
    handleReportError,
    startPolling,
  ]);

  // Cleanup do polling quando o componente desmontar
  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  const columns = useMemo(
    () => [
      {
        id: "eventEditionName",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "events" })}
          </div>
        ),
        selector: (row) => row.eventEditionName,
        sortField: "eventEditionName",
        sortable: true,
        width: "20%",
        cell: (row) => {
          const text = row.eventEditionName || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "documentName",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "documents" })}
          </div>
        ),
        selector: (row) => row.documentName,
        sortField: "documentName",
        sortable: true,
        width: "20%",
        cell: (row) => {
          const text = row.documentName || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "approvedBySystem",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "approved_by_system" })}
          </div>
        ),
        selector: (row) => row.approvedBySystem,
        sortField: "approvedBySystem",
        sortable: true,
        width: "8%",
        center: true,
      },
      {
        id: "pending",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "admin.documents.box_pending_approve" })}
          </div>
        ),
        selector: (row) => row.pending,
        sortField: "pending",
        sortable: true,
        width: "12%",
        center: true,
      },
      {
        id: "approved",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "admin_approved_documents" })}
          </div>
        ),
        selector: (row) => row.approved,
        sortField: "approved",
        sortable: true,
        width: "10%",
        center: true,
      },
      {
        id: "rejected",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "admin_rejected_documents" })}
          </div>
        ),
        selector: (row) => row.rejected,
        sortField: "rejected",
        sortable: true,
        width: "10%",
        center: true,
      },
      {
        id: "awaitingUpload",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "awaiting_send" })}
          </div>
        ),
        selector: (row) => row.awaitingUpload,
        sortField: "awaitingUpload",
        sortable: true,
        width: "10%",
        center: true,
      },
      {
        id: "uploadExcused",
        name: (
          <div style={{ whiteSpace: "normal", textAlign: "center" }}>
            {intl.formatMessage({ id: "dispensed_send" })}
          </div>
        ),
        selector: (row) => row.uploadExcused,
        sortField: "uploadExcused",
        sortable: true,
        width: "10%",
        center: true,
      },
    ],
    [intl],
  );

  return (
    <AnimatedMainContainer
      breadcrumb={`
        ${intl.formatMessage({ id: "sidebar.upload_documents" })} |
        ${"Analytics"}
      `}
    >
      <Container>
        <ContentBordered>
          <HeaderContainer>
            {" "}
            <Title>
              <FormattedMessage id="documents" />
            </Title>
            <Button
              style={{ marginRight: "16px" }}
              Icon={
                loadingDownload ? (
                  <AiOutlineLoading3Quarters />
                ) : (
                  <FiDownload size={18} />
                )
              }
              loading={loadingDownload}
              label={intl.formatMessage({ id: "export_analytics" })}
              variant={"secondary"}
              onClick={() => requestDownloadAnalyticsFile()}
              disabled={
                !selectedYear ||
                tableData.length === 0 ||
                loadingDownload ||
                loadingTable
              }
            />
          </HeaderContainer>
          <FilterContainer>
            <FilterSelect
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder={intl.formatMessage({ id: "edition_year" })}
              value={selectedYear || undefined}
              onChange={(value) => applyYear(value || null)}
              loading={loadingFilters}
            >
              {years.map((year) => (
                <Option key={year} value={year}>
                  {year}
                </Option>
              ))}
            </FilterSelect>

            <FilterSelect
              showSearch
              mode="multiple"
              maxTagCount="responsive"
              optionFilterProp="children"
              disabled={!selectedYear}
              placeholder={`${intl.formatMessage({
                id: "all",
              })} : ${intl.formatMessage({ id: "events" })}`}
              value={selectedEvents}
              onChange={(values) => applyEvents(values || [])}
              loading={loadingFilters}
            >
              {eventsFiltered.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.eventName}
                </Option>
              ))}
            </FilterSelect>

            <FilterSelect
              showSearch
              optionFilterProp="children"
              disabled={!selectedYear}
              value={selectedDoc || ""}
              onChange={(value) => {
                setSelectedDoc(value);
                setCurrentPage(1);
                fetchData(1, rowsPerPage, selectedYear, selectedEvents, value);
              }}
              loading={loadingFilters}
            >
              <Option key="all" value="">
                {intl.formatMessage({ id: "all" })} :{" "}
                {intl.formatMessage({ id: "documents" })}
              </Option>
              {docsFiltered.map((doc) => (
                <Option key={doc.value} value={doc.value}>
                  {doc.label}
                </Option>
              ))}
            </FilterSelect>
          </FilterContainer>

          {!selectedYear && (
            <RequiredFieldHint>
              <FormattedMessage id="edition_year_required" />
            </RequiredFieldHint>
          )}

          <Table
            sortServer={true}
            data={tableData}
            columns={columns}
            progressPending={loadingTable}
            paginationServer={true}
            paginationTotalRows={totalRows}
            paginationCurrentPage={currentPage}
            paginationPerPage={rowsPerPage}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            onSort={(column, dir) => {
              setSortBy(column.sortField);
              setSortDirection(dir);
              fetchData(
                currentPage,
                rowsPerPage,
                selectedYear,
                selectedEvents,
                selectedDoc,
                column.sortField,
                dir,
              );
            }}
          />
        </ContentBordered>
      </Container>
    </AnimatedMainContainer>
  );
};

export default AnalyticsDocuments;
